import { isStructuredAddressPayload, legacyStringToAddressPayload } from '@/types/address';
import { parseItems } from '@/context/cart/cartLines';
export const CART_STORAGE_KEY = 'grand78_cart_v3';
export const PREV_CART_STORAGE_KEY = 'grand78_cart_v2';
export const LEGACY_V1_STORAGE_KEY = 'grand78_cart_v1';
export const LEGACY_MENU_ID = 0;
function parseDeliveryAddress(raw) {
    if (raw == null)
        return null;
    if (typeof raw === 'string') {
        const s = raw.trim();
        return s ? legacyStringToAddressPayload(s) : null;
    }
    if (isStructuredAddressPayload(raw)) {
        return {
            city: raw.city,
            street: raw.street,
            house: raw.house,
            entrance: raw.entrance ?? null,
            floor: raw.floor ?? null,
            apartment: raw.apartment ?? null,
        };
    }
    return null;
}
export function createEmptySlice() {
    return {
        items: [],
        deliveryType: 'delivery',
        deliveryAddress: null,
        pickupRestaurantId: null,
    };
}
function parseStoredSlice(raw) {
    if (!raw || typeof raw !== 'object') {
        return createEmptySlice();
    }
    return {
        items: Array.isArray(raw.items) ? parseItems(raw.items) : [],
        deliveryType: raw.deliveryType === 'pickup' ? 'pickup' : 'delivery',
        deliveryAddress: parseDeliveryAddress(raw.deliveryAddress),
        pickupRestaurantId: typeof raw.pickupRestaurantId === 'number' ? raw.pickupRestaurantId : null,
    };
}
function parseV2Payload(data) {
    const items = Array.isArray(data.items) ? parseItems(data.items) : [];
    const deliveryType = data.deliveryType === 'pickup' ? 'pickup' : 'delivery';
    const deliveryAddress = parseDeliveryAddress(data.deliveryAddress);
    const pickupRestaurantId = typeof data.pickupRestaurantId === 'number' ? data.pickupRestaurantId : null;
    const checkoutMenuId = typeof data.checkoutMenuId === 'number' && Number.isFinite(data.checkoutMenuId)
        ? data.checkoutMenuId
        : null;
    if (checkoutMenuId != null) {
        return {
            activeMenuId: checkoutMenuId,
            cartsByMenuId: {
                [checkoutMenuId]: { items, deliveryType, deliveryAddress, pickupRestaurantId },
            },
        };
    }
    if (items.length > 0) {
        return {
            activeMenuId: LEGACY_MENU_ID,
            cartsByMenuId: {
                [LEGACY_MENU_ID]: { items, deliveryType, deliveryAddress, pickupRestaurantId },
            },
        };
    }
    return { activeMenuId: null, cartsByMenuId: {} };
}
export function parseStoredCartState() {
    try {
        const v3Raw = localStorage.getItem(CART_STORAGE_KEY);
        if (v3Raw) {
            const data = JSON.parse(v3Raw);
            const cartsByMenuId = {};
            if (data.cartsByMenuId && typeof data.cartsByMenuId === 'object') {
                for (const [key, rawSlice] of Object.entries(data.cartsByMenuId)) {
                    const menuId = Number(key);
                    if (!Number.isFinite(menuId))
                        continue;
                    cartsByMenuId[menuId] = parseStoredSlice(rawSlice);
                }
            }
            const activeMenuId = typeof data.activeMenuId === 'number' && Number.isFinite(data.activeMenuId)
                ? data.activeMenuId
                : null;
            return { activeMenuId, cartsByMenuId };
        }
        const v2Raw = localStorage.getItem(PREV_CART_STORAGE_KEY);
        if (v2Raw) {
            return parseV2Payload(JSON.parse(v2Raw));
        }
        const v1Raw = localStorage.getItem(LEGACY_V1_STORAGE_KEY);
        if (v1Raw) {
            return parseV2Payload(JSON.parse(v1Raw));
        }
    }
    catch {
        /* ignore parse errors */
    }
    return { activeMenuId: null, cartsByMenuId: {} };
}
export function serializeCartState(activeMenuId, cartsByMenuId) {
    const cartsPayload = {};
    for (const [menuId, slice] of Object.entries(cartsByMenuId)) {
        cartsPayload[menuId] = {
            items: slice.items,
            deliveryType: slice.deliveryType,
            deliveryAddress: slice.deliveryAddress,
            pickupRestaurantId: slice.pickupRestaurantId,
        };
    }
    return { activeMenuId, cartsByMenuId: cartsPayload };
}
export function persistCartState(activeMenuId, cartsByMenuId) {
    try {
        const payload = serializeCartState(activeMenuId, cartsByMenuId);
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(payload));
        localStorage.removeItem(PREV_CART_STORAGE_KEY);
        localStorage.removeItem(LEGACY_V1_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
}
